import type { Map as MapType } from "mapbox-gl";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import { createSafeFilename } from "senf-shared";
import type { Project } from "senf-shared/types";
import {
  exportChartAsPNG,
  exportChartAsSVG,
  exportMap,
} from "senf-shared/utils/exportData";
import BarChart from "../../atoms/charts/BarChart";
import PostcodeMap from "../../atoms/map/PostcodeMap";
import Dropdown from "../../core/dropdown/Dropdown";
import type { Result } from "../survey/legacy/survey.types";
import ParticipationStructure from "./ParticipationStructure";

const promptHeadline = `Bitte fasse alle Umfrageantworten zusammen und erstelle 1 aussagekräftige Headline, die in einer Zeitung veröffentlicht werden könnten. Verwende vor jeder Überschrift 'h:'.`;
const promptSummary =
  "Bitte fasse alle Umfrageantworten zusammen und erstelle einen 1500 Zeichen Bericht mit Handlungsempfehlungen für die Stadt. Schreibe präzise und direkt die Ergebnisse ohne den Inhalt zu umschreiben";

export const DemographicsChartWrapper = ({
  title,
  dropdownItems,
  children,
}) => {
  return (
    <div className="col-span-6 flex min-h-[310px] min-w-0 flex-col gap-5 rounded-2xl border bg-white p-5">
      <div className="flex items-center justify-between">
        <h3 className="heading-lg">{title}</h3>
        {dropdownItems ? (
          <Dropdown
            variant="shapeless"
            trailingIcon="more"
            closeOnClick={true}
            size="lg"
            className="-my-5 -mx-2"
            items={dropdownItems}
          />
        ) : null}
      </div>
      {children}
    </div>
  );
};

const CustomDemographicFields = ({
  project,
  data,
}: {
  project: Project;
  data: Result[];
}) => {
  const { t } = useTranslation();

  // console.log(project.registrations, data)
  return project?.settings?.demographicData?.fields?.map((field) => {
    // Count occurrences of each value for this field
    const fieldStats = data?.reduce(
      (acc, dataPoint) => {
        const user = dataPoint.userData || dataPoint;
        const value = user?.[field.name];
        if (value) {
          acc[value] = (acc[value] || 0) + 1;
        }
        return acc;
      },
      {} as Record<string, number>,
    );

    // Transform fieldStats into chart data format
    const chartData: {
      [key: string]: string | number | undefined;
      label: string;
      count: number;
      value?: string | number;
      valueLabel: string;
      color: string;
    }[] = Object.entries(fieldStats || {})
      .map(([value, count]) => {
        const label =
          field?.options?.find((o) => o.value === value)?.label || value;
        console.log(
          field?.options?.find((o) => o.value === value)?.label,
          value,
        );
        console.log(label);
        return {
          [label]: count,
          label,
          count,
          value: label,
          valueLabel: `${count} ${t("global:answers")}`,
          color: "var(--surface-depressed)",
        };
      })
      .sort((a, b) => b.count - a.count);

    const INITIAL_ITEMS = 3; // Initial number of items to show
    const ITEMS_PER_PAGE = 5; // Number of items to show per "Show More" click

    const [displayCount, setDisplayCount] = useState(INITIAL_ITEMS);
    // const [showInitialOnly, setShowInitialOnly] = useState(true);

    const displayData = chartData.slice(
      0,
      chartData.length > INITIAL_ITEMS
        ? displayCount === chartData.length
          ? displayCount
          : displayCount - 1
        : displayCount,
    );
    const other = chartData.slice(displayData.length);

    if (displayData.length < chartData.length) {
      const total = other.reduce((acc, curr) => acc + curr.count, 0);
      const otherLabel = t("global:other");
      const [base] = other;
      displayData.push({
        ...other.reduce((acc, curr) => {
          if (curr.value) acc[curr.value] = curr.count;
          return acc;
        }, {}),
        [otherLabel]: total,
        label: otherLabel,
        count: total,
        color: base.color,
        valueLabel: `${total} ${t("global:answers")}`,
      });
    }

    displayData.forEach((e, i, arr) => {
      const colorVal =
        arr.length > 9
          ? 5
          : Math.max(1, arr.length - i) *
            Math.floor(7 / Math.min(7, arr.length));
      e.color = `var(--primary-${colorVal * 100})`;
      return e;
    });

    // Calculate dynamic height based on number of items
    const itemHeight = 20; // Height per chart item
    const minHeight = 240; // Minimum container height
    const currentHeight = displayData.length * itemHeight;
    const dynamicHeight = Math.max(minHeight, currentHeight);
    const demographicFieldRef = useRef<HTMLElement>(null);
    const demographicDropdown = [
      {
        text: t("util:downloadAs", {
          val: t("global:chart"),
          format: "PNG",
        }),
        leadingIcon: "download",
        onClick: () =>
          exportChartAsPNG({
            ref: demographicFieldRef,
            fileName: `${createSafeFilename([
              t("global:demographicDataByAgeAndGender"),
              project?.internalProjectTitle,
            ])}`,
            downloadImmediately: true,
          }),
      },
      {
        text: t("util:downloadAs", {
          val: t("global:chart"),
          format: "SVG",
        }),
        leadingIcon: "download",
        onClick: () =>
          exportChartAsSVG({
            ref: demographicFieldRef,
            fileName: `${createSafeFilename([
              t("global:demographicDataByAgeAndGender"),
              project?.internalProjectTitle,
            ])}`,
            downloadImmediately: true,
          }),
      },
    ];

    return (
      <DemographicsChartWrapper
        title={field.label}
        key={field.name}
        dropdownItems={demographicDropdown}
      >
        {displayData?.length || displayData?.length ? (
          <>
            <div className="relative z-10" ref={demographicFieldRef}>
              <BarChart
                isAnimationActive={false}
                stacked
                height={dynamicHeight}
                bars={chartData}
                data={displayData}
              />
            </div>
            {chartData.length > INITIAL_ITEMS && (
              <div className="flex justify-center gap-4">
                <Button
                  variant="basic"
                  onClick={() => {
                    setDisplayCount(
                      Math.min(displayCount + ITEMS_PER_PAGE, chartData.length),
                    );
                  }}
                  disabled={displayCount >= chartData.length}
                  text={t("util:show", { val: t("global:more") })}
                />

                <Button
                  variant="basic"
                  disabled={displayCount <= INITIAL_ITEMS}
                  onClick={() => {
                    setDisplayCount(
                      Math.max(displayCount - ITEMS_PER_PAGE, INITIAL_ITEMS),
                    );
                  }}
                  text={t("util:show", { val: t("global:fewer") })} // @todo: add global:fewer and global:less
                />
              </div>
            )}
          </>
        ) : (
          <div className="bg-surface-subdued mb-5 flex w-full grow items-center justify-center rounded-xl p-5">
            <span className="body-md text-text-subdued text-center">
              {t("util:nothingToShowYet", {
                val: t("global:data"),
              })}
            </span>
          </div>
        )}
      </DemographicsChartWrapper>
    );
  });
};

const DemographicsResults = ({
  downloadCharts,
  project,
  data,
  className,
}: {
  downloadCharts?: boolean;
  project: Project;
  data: Result[];
  className?: string;
}) => {
  const { t } = useTranslation();
  const [statefulMap, setStatefulMap] = useState<MapType>();
  const participationChartRef = useRef(null);

  const participationStructionDropdown = downloadCharts && [
    {
      text: t("util:downloadAs", {
        val: t("global:chart"),
        format: "PNG",
      }),
      leadingIcon: "download",
      onClick: () =>
        exportChartAsPNG({
          ref: participationChartRef,
          fileName: `${createSafeFilename([
            t("global:demographicDataByAgeAndGender"),
            project?.internalProjectTitle,
          ])}`,
          downloadImmediately: true,
        }),
    },
    {
      text: t("util:downloadAs", {
        val: t("global:chart"),
        format: "SVG",
      }),
      leadingIcon: "download",
      onClick: () =>
        exportChartAsSVG({
          ref: participationChartRef,
          fileName: `${createSafeFilename([
            t("global:demographicDataByAgeAndGender"),
            project?.internalProjectTitle,
          ])}`,
          downloadImmediately: true,
        }),
    },
  ];

  const postCodeDropdown = downloadCharts && [
    {
      text: t("util:downloadAs", {
        val: t("global:map"),
        format: "PNG",
      }),
      leadingIcon: "download",
      onClick: () =>
        statefulMap
          ? exportMap(
              statefulMap,
              `${createSafeFilename([
                t("global:map"),
                t("global:demographicDataByPostcodes"),
                project?.internalProjectTitle,
              ])}`,
            )
          : {},
    },
  ];

  return (
    <div className={className}>
      <DemographicsChartWrapper
        title={t("global:demographicDataByAgeAndGender")}
        dropdownItems={participationStructionDropdown}
      >
        <ParticipationStructure
          ref={participationChartRef}
          data={data}
          project={project}
        />
      </DemographicsChartWrapper>

      {!project?.settings?.demographicData?.withoutPostcode && (
        <DemographicsChartWrapper
          title={t("global:demographicDataByPostcodes")}
          dropdownItems={postCodeDropdown}
        >
          <div className="h-[500px] w-full overflow-hidden rounded-2xl">
            <PostcodeMap
              project={project}
              data={data}
              statefulMap={statefulMap}
              setStatefulMap={setStatefulMap}
            />
          </div>
        </DemographicsChartWrapper>
      )}

      <CustomDemographicFields project={project} data={data} />
    </div>
  );
};

export default DemographicsResults;
